import { type ComponentProps, type FC, type ReactNode } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { cn } from "../../lib/utils";
import { ClickableIcon } from "../../primitives";
import { MutationErrorFlag } from "../../ui/MutationErrorFlag";
import { MetricCard } from "./MetricCard";

/**
 * Edit state for an {@link EditableMetricCard}, owned by the consumer's feature hook.
 * The card reads this to render the action chrome and error flag; it never holds its own state.
 */
export interface EditableMetricController {
	isEditing: boolean;
	/** Gates the confirm icon — when false it renders disabled and clicking is a no-op. */
	canSubmit: boolean;
	/** Gates the cancel icon — set false while a submit is in flight so the request can't be abandoned mid-commit. Defaults to enabled. */
	canCancel?: boolean;
	mutationError: Error | null;
	startEditing: () => void;
	cancelEditing: () => void;
	submit: () => void;
	dismissError: () => void;
}

export type EditableMetricCardProps = {
	/** Root `data-testid`, and the prefix for the `-edit-icon`/`-confirm-icon`/`-cancel-icon` test ids. */
	testId: string;
	label: string;
	controller: EditableMetricController;
	/** Metric body — render display vs. edit yourself based on `controller.isEditing`. */
	children: ReactNode;
	/** When true the card is display-only — no edit affordance is rendered. */
	readOnly?: boolean;
	/** Heading for the {@link MutationErrorFlag} surfaced when `controller.mutationError` is set. */
	errorHeading?: string;
	/** Forwarded to the `MetricCard` root (e.g. `"gap-1"`). */
	className?: string;
	/** Forwarded to the `MetricCard.Label` — e.g. reserve a fixed multi-line height so values align across a grid. */
	labelClassName?: string;
	/** Forwarded to the `MetricCard.Label` size variant. */
	labelSize?: ComponentProps<typeof MetricCard.Label>["size"];
	/** Forwarded to the `MetricCard` root to emit `data-pendo-id`/`data-amp-track-id`. */
	trackingId?: string;
} & ConfirmTooltipProps;

/**
 * Confirm-icon tooltip. Both ids stay explicit (not derived) so they're stable across renders:
 * when set, the confirm icon is wrapped with the tooltip anchor and a `<ReactTooltip>` renders
 * while `!controller.canSubmit`. The two props are paired — supply both or neither.
 */
type ConfirmTooltipProps =
	| { confirmTooltipId: string; confirmTooltipContent: string }
	| { confirmTooltipId?: never; confirmTooltipContent?: never };

/** Editable metric tile composing the `MetricCard` surface with edit chrome driven by an {@link EditableMetricController}. */
const EditableMetricCardRoot: FC<EditableMetricCardProps> = ({
	testId,
	label,
	controller,
	children,
	readOnly = false,
	errorHeading,
	className,
	labelClassName,
	labelSize,
	trackingId,
	confirmTooltipId,
	confirmTooltipContent,
}) => {
	const {
		isEditing,
		canSubmit,
		canCancel,
		mutationError,
		startEditing,
		cancelEditing,
		submit,
		dismissError,
	} = controller;

	const confirmIcon = (
		<ClickableIcon
			data-testid={`${testId}-confirm-icon`}
			type="check"
			color={canSubmit ? "confirm" : "subtle"}
			size="mini"
			onClick={submit}
			disabled={!canSubmit}
		/>
	);

	const action = readOnly ? undefined : isEditing ? (
		<div className="flex items-center gap-1 mt-1">
			{confirmTooltipId ? (
				<span
					className="flex"
					data-tooltip-id={confirmTooltipId}
					data-tooltip-content={confirmTooltipContent}
				>
					{confirmIcon}
				</span>
			) : (
				confirmIcon
			)}
			<ClickableIcon
				data-testid={`${testId}-cancel-icon`}
				type="x-mark"
				color="primary"
				size="mini"
				onClick={cancelEditing}
				disabled={canCancel === false}
			/>
		</div>
	) : (
		<ClickableIcon
			data-testid={`${testId}-edit-icon`}
			type="pencil"
			color="primary"
			size="micro"
			onClick={startEditing}
		/>
	);

	return (
		<>
			<MetricCard
				data-testid={testId}
				action={action}
				className={className}
				trackingId={trackingId}
			>
				<MetricCard.Label
					size={labelSize}
					className={cn(!readOnly && (isEditing ? "pr-12" : "pr-7"), labelClassName)}
				>
					{label}
				</MetricCard.Label>
				{children}
			</MetricCard>
			<MutationErrorFlag
				errorHeading={errorHeading}
				mutationError={mutationError}
				dismissError={dismissError}
			/>
			{confirmTooltipId && isEditing && !canSubmit && (
				<ReactTooltip id={confirmTooltipId} style={{ zIndex: 9999 }} />
			)}
		</>
	);
};

/**
 * The `MetricCard` body slots, re-exposed so consumers compose the edit/display body
 * (`EditableMetricCard.Value`, `.Caption`, …) without importing `MetricCard` directly.
 * `Label` is owned by the card and driven by the `label` prop, so it is intentionally omitted.
 */
export const EditableMetricCard = Object.assign(EditableMetricCardRoot, {
	Value: MetricCard.Value,
	ValueRow: MetricCard.ValueRow,
	Caption: MetricCard.Caption,
	Trend: MetricCard.Trend,
});
